import React from 'react'
import { HotelForm } from 'src/components/pages/sales/reservation/logic/hotel/HotelForm'
import { AirForm } from 'src/components/pages/sales/reservation/logic/air/AirForm'
import { CarForm } from 'src/components/pages/sales/reservation/logic/car/CarForm'
import { TransferForm } from 'src/components/pages/sales/reservation/logic/transfer/TransferForm'
import { TrainSegmentForm } from 'src/components/pages/sales/reservation/logic/train/TrainSegmentForm'
import { OtherForm } from 'src/components/pages/sales/reservation/logic/other/OtherForm'
import { AssistanceForm } from 'src/components/pages/sales/reservation/logic/assistance/AssistanceForm'
import { TrainPassesForm } from '../train/TrainPassesForm'
import { ComplementaryItemModal } from 'src/components/pages/sales/reservation/logic/modals/ComplementaryItemModal'

// Opciones válidas de formulario custom para productos online.
const OFFLINE_FORMS_REGISTRY: Record<string, React.ComponentType<any>> = {
  ComplementaryItemModal: ComplementaryItemModal, // para Servicios Varios
}

interface ClientRelation {
  hash_client: string
  id: number
}

interface CurrencyRelationDetails {
  long_name: string
  id: number
  symbol: string
  short_name: string
  hash_currency: string
}

interface CurrencyRelation {
  value: number
  currency_to_id: number
  currency_id: number
  is_reverse: boolean
  work_unit_id: number
  currency_to_relation: CurrencyRelationDetails
  currency_relation: CurrencyRelationDetails
}

interface WotkUnit {
  token: string
  config: null | any
  short_name: string
  long_name: string
  description: string
  client_id: number
  parent_id: null | number
  id: number
  is_active: boolean
  hash_work_unit: string
  reservation_numbering_id: number
  client_numbering_id: number
  client_relation: ClientRelation
  currency_relation: CurrencyRelation[]
  children: any[]
}

interface IReservation {
  work_unit_id: number
  number: string
  status: string
  business_unit_id: number
  id: number
  business_unit_relation: object
  work_unit_relation: WotkUnit
  product: object
  user: string
  passenger: string
  check_in: string
  amount_total: number
  amount_pending: number
  items: object
}

interface ChoiceProductModalProps {
  openModal: boolean
  product: string
  reservation: IReservation
  passengers: object[]
  selectedWorkUnit: WotkUnit
  selectedProduct: any | null
  handleCloseProductModal: () => void
  onSubmitProduct: (product: string, data: object, isOther: boolean) => void | null
  record: object | null
}

export const ChoiceProductModal: React.FC<ChoiceProductModalProps> = ({
  openModal,
  product,
  reservation,
  passengers,
  selectedWorkUnit,
  selectedProduct,
  handleCloseProductModal,
  onSubmitProduct,
  record,
}) => {
  if (product === 'hotel_detail') {
    return (
      <HotelForm
        openModal={openModal}
        reservation={reservation}
        passengers={passengers}
        workUnit={selectedWorkUnit}
        product={selectedProduct}
        handleClose={handleCloseProductModal}
        onSubmit={onSubmitProduct ? (data: any) => onSubmitProduct('hotel', data, false) : null}
        recordProduct={record}
      />
    )
  } else if (product === 'car_detail') {
    return (
      <CarForm
        openModal={openModal}
        reservation={reservation}
        passengers={passengers}
        workUnit={selectedWorkUnit}
        product={selectedProduct}
        handleClose={handleCloseProductModal}
        onSubmit={onSubmitProduct ? (data: any) => onSubmitProduct('car', data, false) : null}
        recordProduct={record}
      />
    )
  } else if (product === 'assistance_detail') {
    return (
      <AssistanceForm
        openModal={openModal}
        reservation={reservation}
        passengers={passengers}
        workUnit={selectedWorkUnit}
        product={selectedProduct}
        handleClose={handleCloseProductModal}
        onSubmit={onSubmitProduct ? (data: any) => onSubmitProduct('assistance', data, false) : null}
        recordProduct={record}
      />
    )
  } else if (product === 'transfer_detail') {
    return (
      <TransferForm
        openModal={openModal}
        reservation={reservation}
        passengers={passengers}
        workUnit={selectedWorkUnit}
        product={selectedProduct}
        handleClose={handleCloseProductModal}
        onSubmit={onSubmitProduct ? (data: any) => onSubmitProduct('transfer', data, false) : null}
        recordProduct={record}
      />
    )
  } else if (product === 'air_detail') {
    return (
      <AirForm
        openModal={openModal}
        reservation={reservation}
        passengers={passengers}
        workUnit={selectedWorkUnit}
        product={selectedProduct}
        handleClose={handleCloseProductModal}
        onSubmit={onSubmitProduct ? (data: any) => onSubmitProduct('flight', data, false) : null}
        recordProduct={record}
      />
    )
  } else if (product === 'other_detail') {
    const rawParams = selectedProduct?.product_relation?.parameters
    let parameters: any = {}
    if (typeof rawParams === 'string') {
      try {
        parameters = JSON.parse(rawParams)
      } catch (e) {
        parameters = {}
      }
    } else if (typeof rawParams === 'object' && rawParams !== null) {
      parameters = rawParams
    }
    // Verifica si posee una config para mostrar un formulario custom por producto (solo para "other_detail").
    const customFormKey = parameters?.offline_create_form
    const CustomFormComponent = customFormKey ? OFFLINE_FORMS_REGISTRY[customFormKey] : null

    if (CustomFormComponent) {
      return (
        <CustomFormComponent
          open={openModal}
          reservation={reservation}
          selectedProduct={selectedProduct}
          onClose={handleCloseProductModal}
          onSubmitSuccess={() => {
            handleCloseProductModal()
            if (onSubmitProduct) {
              onSubmitProduct(selectedProduct?.product_relation?.long_name, {}, true)
            }
          }}
        />
      )
    }

    return (
      <OtherForm
        openModal={openModal}
        reservation={reservation}
        passengers={passengers}
        workUnit={selectedWorkUnit}
        product={selectedProduct}
        handleClose={handleCloseProductModal}
        onSubmit={
          onSubmitProduct
            ? (data: any) => onSubmitProduct(selectedProduct?.product_relation?.long_name, data, true)
            : null
        }
        recordProduct={record}
      />
    )
  } else if (product === 'train_detail_segment') {
    return (
      <TrainSegmentForm
        openModal={openModal}
        reservation={reservation}
        passengers={passengers || []}
        workUnit={selectedWorkUnit}
        product={selectedProduct}
        handleClose={handleCloseProductModal}
        onSubmit={(data: any) => onSubmitProduct('train_segment', data, false)}
        recordProduct={record}
      />
    )
  } else if (product === 'train_detail_passes') {
    return (
      <TrainPassesForm
        openModal={openModal}
        reservation={reservation}
        passengers={passengers}
        workUnit={selectedWorkUnit}
        product={selectedProduct}
        handleClose={handleCloseProductModal}
        onSubmit={(data: any) => onSubmitProduct('train_passes', data, false)}
        recordProduct={record}
      />
    )
  }
  return null
}
